import { useState, useContext, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../Context/AuthContext";

const useVerificationCodeForm = () => {
  const { userType, login } = useContext(AuthContext);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [verificationCode, setVerificationCode] = useState(["", "", "", "", "", ""]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoading2, setIsLoading2] = useState(false);
  const [error, setError] = useState(null);
  const [resendTimer, setResendTimer] = useState(30);
  const navigate = useNavigate();
  const inputRefs = useRef([]);

  const handleShow = () => {
    setShow(!show);
  };

  const getSessionStorageWithExpiry = (key) => {
    const data = JSON.parse(sessionStorage.getItem(key));
    if (!data) return null;
    if (data.expiry < new Date().getTime()) {
      sessionStorage.removeItem(key);
      return null;
    }
    return data.value;
  };

  useEffect(() => {
    const storedEmail = getSessionStorageWithExpiry("userEmail");
    const storedPassword = getSessionStorageWithExpiry("userPassword");
    if (storedEmail) setEmail(storedEmail);
    if (storedPassword) setPassword(storedPassword);
  }, []);
  

  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, verificationCode.length);
  }, [verificationCode.length]);

  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; 
    
    const newCode = [...verificationCode];
    newCode[index] = value.slice(-1); 
    setVerificationCode(newCode);
    setError(false);
  
   
    if (value !== "" && index < verificationCode.length - 1) {
      inputRefs.current[index + 1].focus();
    }
  
    if (value === "" && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };
  
  const handlePaste = (event) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData("text").trim().slice(0, 6); 
    const newCode = [...verificationCode];
  
    for (let i = 0; i < pastedData.length; i++) {
      if (i < verificationCode.length) {
        newCode[i] = pastedData[i];
      }
    }
  
    setVerificationCode(newCode);
  
    // Focus the last filled input
    const lastFilledIndex = Math.min(pastedData.length - 1, verificationCode.length - 1);
    inputRefs.current[lastFilledIndex]?.focus();
  };
  

  const isInputEmpty = () => verificationCode.some((digit) => digit === "");

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setIsLoading(true);
      setError(null);

      if (isInputEmpty() || !email || !password) {
        throw new Error("Please fill in all the fields");
      }
      const codeString = verificationCode.join("");

      let endpoint = "";
      if (userType === "Charterer") {
        endpoint = `${process.env.REACT_APP_SERVER_URL}/auth/verifyEmail/charterer`;
      } else if (userType === "Vessel Owner") {
        endpoint = `${process.env.REACT_APP_SERVER_URL}/auth/verifyEmail/vesselOwner`;
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password, OTP: codeString }),
      });
      const resData = await response.json();

      if (!response.ok) {
        throw new Error(resData.message);
      }

      const profileData = {
        role: resData.data.user.role,
        userId: resData.data.user._id,
        token: resData.token,
      };

      login(profileData);

      if (resData.data.user.role === "charterer") {
        const profileStepStatus = resData.data.user.createdProfileStepStatus?.status;

        if (profileStepStatus === "1") {
          navigate("/statutorydocs");
        } else if (profileStepStatus === "2") {
          navigate("/license");
        } else if (profileStepStatus === "3") {
          navigate("/otherdocs");
        } else if (profileStepStatus === undefined || profileStepStatus === null) {
          navigate("/Customeractivationpage1");
        } else {
          navigate("/");
        }
      } else if (resData.data.user.role === "vesselOwner") {
        const profileStepStatus = resData.data.user.createdProfileStepStatus?.status;

        if (profileStepStatus === "1") {
          navigate("/statutory_v_doc");
        } else if (profileStepStatus === "2") {
          navigate("/licenses_v");
        } else if (profileStepStatus === "3") {
          navigate("/otherdocs_v");
        } else if (profileStepStatus === undefined || profileStepStatus === null) {
          navigate("/createprofile_step_1");
        } else {
          navigate("/");
        }
      } else {
        navigate("/");
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setIsLoading2(true);
      setError(null);

      let endpoint = "";
      if (userType === "Charterer") {
        endpoint = `${process.env.REACT_APP_SERVER_URL}/auth/resendValidationEmail/charterer`;
      } else if (userType === "Vessel Owner") {
        endpoint = `${process.env.REACT_APP_SERVER_URL}/auth/resendValidationEmail/vesselOwner`;
      }

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const resData = await response.json();

      if (!response.ok) {
        throw new Error(resData.message);
      }

      setResendTimer(30);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading2(false);
    }
  };

  useEffect(() => {
    const countdown = setInterval(() => {
      if (resendTimer > 0) {
        setResendTimer((prevTimer) => prevTimer - 1);
      }
    }, 1000);

    return () => clearInterval(countdown);
  }, [resendTimer]);

  return {
    email,
    password,
    verificationCode,
    handleChange,
    handleSubmit,
    isLoading,
    isLoading2,
    error,
    resendTimer,
    handleResend,
    inputRefs,
    handleShow,
    show,
    handlePaste
  };
};

export default useVerificationCodeForm;

